import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar,
} from 'recharts';
import 'iconify-icon';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const authHeaders = () => {
  const t = localStorage.getItem('access_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const ORANGE = '#FF6700';
const NAVY = '#0a0a0a';
const TEAL = '#06b6d4';

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [counts, setCounts] = useState(null);
  const [timeseries, setTimeseries] = useState(null);
  const [leadsByCity, setLeadsByCity] = useState([]);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  const loadAll = useCallback(async () => {
    try {
      const opt = { withCredentials: true, headers: authHeaders() };
      const [franchises, events, team, blog, testimonials, faqs, eneaflix, submissionsCounts, ts, leads] = await Promise.all([
        axios.get(`${API_URL}/admin/franchises`, opt),
        axios.get(`${API_URL}/admin/events`, opt),
        axios.get(`${API_URL}/admin/team`, opt),
        axios.get(`${API_URL}/admin/blog`, opt),
        axios.get(`${API_URL}/admin/testimonials`, opt),
        axios.get(`${API_URL}/admin/faq`, opt),
        axios.get(`${API_URL}/admin/eneaflix`, opt),
        axios.get(`${API_URL}/admin/submissions-counts`, opt),
        axios.get(`${API_URL}/admin/analytics/submissions-timeseries?days=${days}`, opt),
        axios.get(`${API_URL}/admin/analytics/leads-by-franchise`, opt),
      ]);

      setStats({
        franchises: franchises.data.length,
        events: events.data.length,
        teamMembers: team.data.length,
        blogPosts: blog.data.length,
        testimonials: testimonials.data.length,
        faqs: faqs.data.length,
        eneaflix: eneaflix.data.length,
      });
      setCounts(submissionsCounts.data);
      // Transform timeseries for recharts
      const chartData = ts.data.days.map((day, i) => ({
        date: day.slice(5), // MM-DD
        Contato: ts.data.series.contact[i],
        Franqueado: ts.data.series.franchisee[i],
        Trabalhe: ts.data.series.job[i],
      }));
      setTimeseries(chartData);
      setLeadsByCity(leads.data);
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const statsCards = stats ? [
    { icon: 'solar:map-point-bold', label: 'Franquias', value: stats.franchises },
    { icon: 'solar:calendar-bold', label: 'Eventos', value: stats.events },
    { icon: 'solar:users-group-rounded-bold', label: 'Equipe', value: stats.teamMembers },
    { icon: 'solar:document-text-bold', label: 'Blog', value: stats.blogPosts },
    { icon: 'solar:chat-square-like-bold', label: 'Depoimentos', value: stats.testimonials },
    { icon: 'solar:question-square-bold', label: 'FAQs', value: stats.faqs },
    { icon: 'solar:videocamera-bold', label: 'ENEAFLIX', value: stats.eneaflix },
  ] : [];

  const leadStats = counts ? [
    { key: 'contact', icon: 'solar:letter-bold', label: 'Contato', ...counts.contact },
    { key: 'franchisee', icon: 'solar:case-bold', label: 'Franqueado', ...counts.franchisee },
    { key: 'job', icon: 'solar:user-id-bold', label: 'Trabalhe Conosco', ...counts.job },
  ] : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-8">
        <h2 className="text-3xl font-bold mb-2">Dashboard</h2>
        <p className="text-neutral-600">Visão geral do conteúdo e dos leads recebidos</p>
      </div>

      {/* Leads counters (highlighted) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        {leadStats.map((s) => (
          <Link
            key={s.key}
            to="/admin/submissions"
            data-testid={`dashboard-lead-${s.key}`}
            className="accent-glow-card bg-white p-6 rounded-xl border border-neutral-200 flex items-center gap-4"
          >
            <div className="w-14 h-14 rounded-full bg-accent-orange/10 flex items-center justify-center text-accent-orange">
              <iconify-icon icon={s.icon} className="text-3xl"></iconify-icon>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs text-neutral-500 uppercase tracking-widest font-mono">{s.label}</div>
              <div className="text-3xl font-bold">{s.total}</div>
              {s.new > 0 && <div className="text-xs text-accent-orange font-medium mt-1">{s.new} novo{s.new > 1 ? 's' : ''}</div>}
            </div>
            <iconify-icon icon="solar:arrow-right-up-linear" className="text-2xl text-zinc-700"></iconify-icon>
          </Link>
        ))}
      </div>

      {/* Time series chart */}
      <div className="bg-white rounded-xl border border-neutral-200 p-6 mb-8" data-testid="dashboard-timeseries">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold">Submissões ao longo do tempo</h3>
            <p className="text-sm text-neutral-500">Tendência diária por tipo de formulário</p>
          </div>
          <div className="flex gap-1 bg-neutral-100 rounded-lg p-1">
            {[7, 30, 90].map((d) => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={`px-3 py-1.5 text-xs font-mono uppercase rounded-md transition-all ${
                  days === d ? 'bg-white shadow-sm text-black' : 'text-neutral-500 hover:text-black'
                }`}
              >
                {d} dias
              </button>
            ))}
          </div>
        </div>
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={timeseries || []} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
            <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#737373' }} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#737373' }} />
            <Tooltip
              contentStyle={{ background: '#fff', border: '1px solid #e5e5e5', borderRadius: 8, fontSize: 12 }}
              cursor={{ stroke: ORANGE, strokeWidth: 1 }}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Line type="monotone" dataKey="Contato" stroke={NAVY} strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="Franqueado" stroke={ORANGE} strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="Trabalhe" stroke={TEAL} strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Leads by city/franchise */}
        <div className="bg-white rounded-xl border border-neutral-200 p-6" data-testid="dashboard-leads-by-city">
          <h3 className="text-lg font-bold mb-1">Leads por cidade</h3>
          <p className="text-sm text-neutral-500 mb-4">Top 12 — solicitações de franqueado</p>
          {leadsByCity.length === 0 ? (
            <div className="py-10 text-center text-sm text-zinc-700">
              <iconify-icon icon="solar:chart-square-linear" className="text-4xl mb-2"></iconify-icon>
              <p>Sem dados ainda.</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={leadsByCity} layout="vertical" margin={{ left: 80 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: '#737373' }} />
                <YAxis dataKey="label" type="category" tick={{ fontSize: 11, fill: '#0a0a0a' }} width={75} />
                <Tooltip
                  contentStyle={{ background: '#fff', border: '1px solid #e5e5e5', borderRadius: 8, fontSize: 12 }}
                  cursor={{ fill: 'rgba(255,103,0,0.08)' }}
                />
                <Bar dataKey="count" fill={ORANGE} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Quick actions */}
        <div className="bg-white rounded-xl border border-neutral-200 p-6">
          <h3 className="text-lg font-bold mb-1">Ações rápidas</h3>
          <p className="text-sm text-neutral-500 mb-4">Atalhos do dia-a-dia</p>
          <div className="grid grid-cols-2 gap-3">
            <Link to="/admin/franchises" className="accent-glow-card p-4 border-2 border-dashed border-neutral-300 rounded-lg block">
              <iconify-icon icon="solar:add-circle-linear" className="text-3xl mb-2 text-accent-orange"></iconify-icon>
              <h4 className="font-semibold text-sm">Nova Franquia</h4>
            </Link>
            <Link to="/admin/blog" className="accent-glow-card p-4 border-2 border-dashed border-neutral-300 rounded-lg block">
              <iconify-icon icon="solar:document-add-linear" className="text-3xl mb-2 text-accent-orange"></iconify-icon>
              <h4 className="font-semibold text-sm">Novo Post</h4>
            </Link>
            <Link to="/admin/events" className="accent-glow-card p-4 border-2 border-dashed border-neutral-300 rounded-lg block">
              <iconify-icon icon="solar:calendar-add-linear" className="text-3xl mb-2 text-accent-orange"></iconify-icon>
              <h4 className="font-semibold text-sm">Novo Evento</h4>
            </Link>
            <Link to="/admin/submissions" className="accent-glow-card p-4 border-2 border-dashed border-neutral-300 rounded-lg block">
              <iconify-icon icon="solar:inbox-in-linear" className="text-3xl mb-2 text-accent-orange"></iconify-icon>
              <h4 className="font-semibold text-sm">Ver submissões</h4>
            </Link>
          </div>
        </div>
      </div>

      {/* Content stats */}
      <div className="bg-white rounded-xl border border-neutral-200 p-6">
        <h3 className="text-lg font-bold mb-4">Conteúdo publicado</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          {statsCards.map((stat, i) => (
            <div key={i} className="text-center p-4 bg-neutral-50 rounded-lg">
              <iconify-icon icon={stat.icon} className="text-3xl text-zinc-700 mb-2"></iconify-icon>
              <div className="text-2xl font-bold">{stat.value}</div>
              <div className="text-xs text-neutral-500 uppercase tracking-widest font-mono mt-1">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
